import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { trackPurchase } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/store/checkout-status/${sessionId}`);
        if (stop) return;
        if (data.payment_status === "paid") {
          setOrder(data.order); setState("paid"); clearCart();
          if (data.order) trackPurchase({ transactionId: data.order.id, value: data.order.total_gbp, itemName: "Herb Store Order" });
          return;
        }
        if (data.status === "expired" || attempts.current >= 8) { setState("failed"); return; }
      } catch { if (attempts.current >= 8) { setState("failed"); return; } }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="store-success-page">
      <div className="max-w-md w-full text-center bg-white border border-gray-100 rounded-3xl shadow-sm p-10">
        {state === "checking" && (<>
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h1 className="font-playfair text-2xl font-bold text-gray-900">Confirming your payment…</h1>
          <p className="text-gray-500 mt-2 text-sm">This only takes a few seconds.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-playfair text-3xl font-bold text-gray-900" data-testid="order-confirmed-heading">Order confirmed!</h1>
          {order && <p className="text-gray-600 mt-3">Order <b>#{order.id.slice(0, 8).toUpperCase()}</b> · £{order.total_gbp.toFixed(2)}</p>}
          <p className="text-gray-500 text-sm mt-2">A confirmation email is on its way. Your herbs typically ship within 3–7 working days.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/account"><Button variant="outline" className="rounded-full" data-testid="view-orders-btn">View my orders</Button></Link>
            <Link to="/store"><Button className="rounded-full">Keep browsing</Button></Link>
          </div>
        </>)}
        {state === "failed" && (<>
          <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="font-playfair text-2xl font-bold text-gray-900">Payment not confirmed</h1>
          <p className="text-gray-500 text-sm mt-2">If you were charged, your order will appear in your account shortly. Otherwise, please try again.</p>
          <Link to="/cart"><Button className="rounded-full mt-6">Back to cart</Button></Link>
        </>)}
      </div>
    </div>
  );
}
